import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search, Users, Loader2, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useStartImpersonation } from '../../store/impersonation.store';
import { Button } from '../../components/ui/Button';

interface CompanyMember {
  user_id: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  avatar_url: string | null;
  role: string | null;
  is_active: boolean | null;
  account_type: string | null;
  global_status: string | null;
  onboarding_complete: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}

interface CompanyMembersViewProps {
  companyId: string;
  companyName: string | null;
  onClose: () => void;
}

export default function CompanyMembersView({ companyId, companyName, onClose }: CompanyMembersViewProps) {
  const navigate = useNavigate();
  const startImpersonation = useStartImpersonation();
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState<CompanyMember | null>(null);
  const [impersonationReason, setImpersonationReason] = useState('');
  const [startingImpersonation, setStartingImpersonation] = useState(false);

  useEffect(() => {
    loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const loadMembers = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use the same query pattern as CustomerDashboard for consistency
      const { data: usersData, error: usersError } = await supabase
        .from('user_company_relationships')
        .select(`
          user_id,
          onboarding_complete,
          created_at,
          system_roles!inner(role),
          user_profiles!inner(email, first_name, last_name, avatar_url, is_active, account_type, global_status)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (usersError) {
        console.error('[CompanyMembersView] Query error:', usersError);
        setError(usersError.message);
        return;
      }

      // Format data to match CompanyMember interface
      type RelationshipRow = {
        user_id: string;
        onboarding_complete?: boolean | null;
        created_at?: string | null;
        system_roles?: { role: string } | null;
        user_profiles?: { 
          first_name?: string | null; 
          last_name?: string | null; 
          email?: string | null;
          avatar_url?: string | null;
          is_active?: boolean | null;
          account_type?: string | null;
          global_status?: string | null;
        } | null;
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formattedMembers: CompanyMember[] = ((usersData || []) as any[]).map((rel: RelationshipRow) => ({
        user_id: rel.user_id,
        email: rel.user_profiles?.email || null,
        first_name: rel.user_profiles?.first_name || null,
        last_name: rel.user_profiles?.last_name || null,
        full_name: rel.user_profiles?.first_name || rel.user_profiles?.last_name
          ? `${rel.user_profiles?.first_name || ''} ${rel.user_profiles?.last_name || ''}`.trim()
          : null,
        avatar_url: rel.user_profiles?.avatar_url || null,
        role: rel.system_roles?.role || null,
        is_active: rel.user_profiles?.is_active ?? null,
        account_type: rel.user_profiles?.account_type || null,
        global_status: rel.user_profiles?.global_status || null,
      } as CompanyMember));

      setMembers(formattedMembers);
    } catch (err) {
      console.error('[CompanyMembersView] Load error:', err);
      setError(err instanceof Error ? err.message : 'load_failed');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'null';
    return new Date(dateStr).toISOString().split('T')[0];
  };

  const filteredMembers = members.filter((member) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.trim().toLowerCase();
    const fullName = member.full_name?.toLowerCase() || '';
    const firstName = member.first_name?.toLowerCase() || '';
    const lastName = member.last_name?.toLowerCase() || '';
    const role = member.role?.toLowerCase() || '';
    const accountType = member.account_type?.toLowerCase() || '';
    return (
      fullName.includes(query) ||
      firstName.includes(query) ||
      lastName.includes(query) ||
      role.includes(query) ||
      accountType.includes(query)
    );
  });

  const handleMemberClick = (member: CompanyMember) => {
    if (!member.user_id) {
      setError('Member has no user_id');
      return;
    }
    setSelectedMember(member);
    setImpersonationReason('');
    setError(null);
  };

  const handleStartImpersonation = async () => {
    if (!selectedMember || !selectedMember.user_id || !impersonationReason.trim()) {
      setError('reason_required');
      return;
    }

    setStartingImpersonation(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('no_session');
        return;
      }

      // Get user email for the session
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('email')
        .eq('id', selectedMember.user_id)
        .single();

      // Create impersonation session record
      const { data: newSession, error: insertError } = await supabase
        .from('impersonation_sessions')
        .insert({
          developer_id: session.user.id,
          impersonated_user_id: selectedMember.user_id,
          reason: impersonationReason.trim(),
          is_active: true,
        })
        .select('id, started_at')
        .single();

      if (insertError) {
        console.error('[CompanyMembersView] Failed to create session:', insertError);
        setError(insertError.message);
        return;
      }

      console.warn('[CompanyMembersView] Session created:', newSession.id);
      
      // Store session in Zustand store
      startImpersonation({
        sessionId: newSession.id,
        adminId: session.user.id,
        impersonatedUserId: selectedMember.user_id,
        impersonatedUserEmail: userProfile?.email || 'unknown',
        startedAt: newSession.started_at,
        reason: impersonationReason.trim(),
        isActive: true,
      });

      // Close modal and navigate to normal user dashboard
      onClose();
      navigate('/dashboard');
    } catch (err) {
      console.error('[CompanyMembersView] Start impersonation error:', err);
      setError(err instanceof Error ? err.message : 'unknown_error');
    } finally {
      setStartingImpersonation(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <header className="bg-[#363232] border-b border-[#504A4A] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-rollops-accent" />
            <div>
              <h2 className="text-xl font-bold text-[#EBFFEE]">Company Members</h2>
              <p className="text-sm text-[#A9A3A3]">
                {companyName || 'Company'} • {members.length} {members.length === 1 ? 'member' : 'members'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[#A9A3A3] hover:text-[#EBFFEE] p-2 hover:bg-[#504A4A] rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {/* Search */}
          <section className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A9A3A3] w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search members by name, role, or account type..."
                className="w-full pl-10 pr-4 py-3 bg-[#1A1818] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                disabled={loading}
              />
            </div>
            {!loading && (
              <div className="text-sm text-[#A9A3A3] mt-2">
                Showing {filteredMembers.length} of {members.length} {members.length === 1 ? 'member' : 'members'}
              </div>
            )}
          </section>

          {/* Error Display */}
          {error && (
            <section className="mb-6">
              <div className="bg-red-950/20 border border-red-900 rounded-lg px-4 py-3 text-text-primary text-sm">
                {error === 'reason_required' && 'Error: Impersonation reason is required'}
                {error === 'no_session' && 'Error: Authentication session not found'}
                {error !== 'reason_required' && error !== 'no_session' && `Error: ${error}`}
              </div>
            </section>
          )}

          {/* Loading State */}
          {loading && (
            <section className="mb-6">
              <div className="flex items-center justify-center py-12">
                <Loader2 size={32} className="animate-spin text-rollops-accent" />
              </div>
            </section>
          )}

          {/* Members Table */}
          {!loading && filteredMembers.length > 0 && (
            <section className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-[#EBFFEE]">Members ({filteredMembers.length})</h3>
                <p className="text-sm text-[#A9A3A3]">Click a member to impersonate</p>
              </div>
              <div className="bg-[#1A1818] border border-[#504A4A] rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#363232]">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#EBFFEE]">Name</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#EBFFEE]">Email</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#EBFFEE]">Role</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#EBFFEE]">Account Type</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#EBFFEE]">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#EBFFEE]">Onboarding</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#EBFFEE]">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map((member) => (
                      <tr
                        key={member.user_id}
                        onClick={() => handleMemberClick(member)}
                        className={`
                          border-t border-[#504A4A] cursor-pointer transition-colors
                          ${selectedMember?.user_id === member.user_id 
                            ? 'bg-[#9B1421]/20' 
                            : 'hover:bg-[#363232]'
                          }
                        `}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {member.avatar_url ? (
                              <img
                                src={member.avatar_url}
                                alt=""
                                className="w-8 h-8 rounded-full"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-[#363232] flex items-center justify-center">
                                <Users size={16} className="text-[#A9A3A3]" />
                              </div>
                            )}
                            <span className="text-sm text-[#EBFFEE]">
                              {member.full_name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#A9A3A3]">{member.email || 'N/A'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs capitalize ${
                            member.role === 'owner' ? 'bg-yellow-600/20 text-yellow-400' : 
                            member.role === 'admin' ? 'bg-[#9B1421]/20 border border-[#9B1421]/40 text-text-primary' : 
                            'bg-[#363232] text-[#EBFFEE]'
                          }`}>
                            {member.role || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#A9A3A3]">{member.account_type || 'N/A'}</td>
                        <td className="px-4 py-3">
                          {member.is_active ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {member.onboarding_complete ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-orange-500" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#A9A3A3]">{formatDate(member.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* No Results */}
          {!loading && filteredMembers.length === 0 && members.length > 0 && (
            <section className="mb-6">
              <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg px-4 py-3 text-[#A9A3A3] text-sm text-center">
                No members found matching search query
              </div>
            </section>
          )}

          {/* No Members */}
          {!loading && members.length === 0 && (
            <section className="mb-6">
              <div className="text-center py-12">
                <Users size={48} className="mx-auto text-[#A9A3A3] mb-4" />
                <p className="text-[#A9A3A3] text-sm">No members found for this company</p>
              </div>
            </section>
          )}

          {/* Selected Member Impersonation Prompt */}
          {selectedMember && (
            <section className="mb-6 mt-6 pt-6 border-t border-[#504A4A]">
              <h3 className="text-lg font-semibold text-[#EBFFEE] mb-2">Impersonate Member</h3>
              <div className="mb-4 text-sm text-[#A9A3A3]">
                {selectedMember.full_name || `${selectedMember.first_name || ''} ${selectedMember.last_name || ''}`.trim() || 'Unknown User'}
                {selectedMember.role && ` • ${selectedMember.role}`}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                  Reason (required for audit)
                </label>
                <textarea
                  value={impersonationReason}
                  onChange={(e) => {
                    setImpersonationReason(e.target.value);
                    setError(null);
                  }}
                  placeholder="e.g., Troubleshooting user issue from company members view"
                  rows={3}
                  className="w-full bg-[#1A1818] border border-[#504A4A] rounded-lg px-4 py-3 text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421] resize-none"
                  disabled={startingImpersonation}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedMember(null);
                    setImpersonationReason('');
                    setError(null);
                  }}
                  disabled={startingImpersonation}
                  className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
                >
                  Cancel
                </Button>
                <Button
                  variant="red"
                  size="sm"
                  onClick={handleStartImpersonation}
                  disabled={startingImpersonation || !impersonationReason.trim()}
                  className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
                >
                  {startingImpersonation ? 'Starting...' : 'Start Impersonation'}
                </Button>
              </div>
            </section>
          )}
        </main>

        {/* Footer Actions */}
        <footer className="border-t border-[#504A4A] px-6 py-4 bg-[#1A1818] flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={loadMembers}
            disabled={loading}
            className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
          >
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
          >
            Close
          </Button>
        </footer>
      </div>
    </div>
  );
}

